import type { Cliente, Meta, Pedido, SubPedido, Visita } from '../db/tipos'
import { arredondar2, dataISOLocal } from '../pedidos/precos'
import { estagioDoPedido, type Estagio } from '../pedidos/estagio'
import { dentroDoPeriodo } from '../metas/calculo'
import { STATUS_VENDA } from '../metas/statusVenda'

// ▶ As contas da tela Inicio (spec 2026-09-29, ciclo 1). Painel.tsx so desenha.

type Totais = { previsto: number; liberado: number; recebido: number }

export function totalDoMes(t: Totais): number {
  return arredondar2(t.previsto + t.liberado + t.recebido)
}

// Mes anterior zerado: nao ha base para comparar, a linha some (spec 2).
// Igual tambem some (decisao dela 01/10): "↑ 0%" verde dizia que subiu.
export function variacaoDaComissao(atual: number, anterior: number): { sentido: 'sobe' | 'desce'; pct: number } | null {
  if (!(anterior > 0)) return null
  const pct = Math.round((Math.abs(atual - anterior) / anterior) * 100)
  if (pct === 0) return null
  return { sentido: atual > anterior ? 'sobe' : 'desce', pct }
}

// Decisao dela 01/10: compara com o MESMO PEDACO do mes passado (do dia 1 ao
// mesmo dia), e nao com o mes inteiro -- no dia 1 a seta dizia "↓ 97%".
export function pedacoDoMesAnterior(hoje: Date): { de: string; ate: string } {
  const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth(), 0).getDate()
  const dia = Math.min(hoje.getDate(), ultimoDia)
  return {
    de: dataISOLocal(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)),
    ate: dataISOLocal(new Date(hoje.getFullYear(), hoje.getMonth() - 1, dia)),
  }
}

export function pctRecebido(t: Totais): number {
  const total = totalDoMes(t)
  if (!(total > 0)) return 0
  return Math.min(100, Math.max(0, Math.round((t.recebido / total) * 100)))
}

// Decisao dela 29/09 (opcao a): o circulo so soma metas em DINHEIRO.
export const TIPOS_EM_DINHEIRO: ReadonlyArray<Meta['tipo']> = ['valor_vendido', 'faturamento_total', 'comissao_necessaria']

export function circuloDasMetas(lista: Array<{ meta: Meta; realizado: number }>) {
  const emDinheiro = lista.filter((m) => TIPOS_EM_DINHEIRO.includes(m.meta.tipo))
  const alvo = arredondar2(emDinheiro.reduce((s, m) => s + m.meta.valor_alvo, 0))
  if (!(alvo > 0)) return null
  const realizado = arredondar2(emDinheiro.reduce((s, m) => s + m.realizado, 0))
  return { realizado, alvo, pct: Math.min(100, Math.round((realizado / alvo) * 100)), falta: arredondar2(Math.max(0, alvo - realizado)) }
}

export type SituacaoDaMeta = 'atingida' | 'em_risco' | 'em_andamento'

// Domingo de Pascoa (algoritmo de Meeus/Jones/Butcher, calendario gregoriano).
function pascoa(ano: number): Date {
  const a = ano % 19, b = Math.floor(ano / 100), c = ano % 100
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451)
  const mes = Math.floor((h + l - 7 * m + 114) / 31)
  return new Date(ano, mes - 1, ((h + l - 7 * m + 114) % 31) + 1)
}

// Feriados NACIONAIS (lei). Carnaval e Corpus Christi sao ponto facultativo e
// ficam de fora; feriado estadual/municipal tambem.
export function feriadosNacionais(ano: number): string[] {
  const p = pascoa(ano)
  const sextaSanta = dataISOLocal(new Date(p.getFullYear(), p.getMonth(), p.getDate() - 2))
  const fixos = ['01-01', '04-21', '05-01', '09-07', '10-12', '11-02', '11-15', '11-20', '12-25']
  return [...fixos.map((md) => `${ano}-${md}`), sextaSanta]
}

// Dias uteis (segunda a sexta, sem feriado nacional) entre duas datas ISO, inclusive.
function diasUteisInclusive(de: string, ate: string): number {
  const [a1, m1, d1] = de.split('-').map(Number)
  const [a2, m2, d2] = ate.split('-').map(Number)
  const fim = new Date(a2, m2 - 1, d2)
  const feriados = new Map<number, Set<string>>()
  let n = 0
  for (const d = new Date(a1, m1 - 1, d1); d <= fim; d.setDate(d.getDate() + 1)) {
    const semana = d.getDay()
    if (semana === 0 || semana === 6) continue
    if (!feriados.has(d.getFullYear())) feriados.set(d.getFullYear(), new Set(feriadosNacionais(d.getFullYear())))
    if (!feriados.get(d.getFullYear())!.has(dataISOLocal(d))) n++
  }
  return n
}

// Decisao dela 02/10 (troca a de 01/10, que contava todos os dias): a meta e
// FRACIONADA POR DIA UTIL -- segunda a sexta, sem feriado nacional -- e so os
// dias COMPLETOS, ate ontem. No primeiro dia util ninguem "deve" nada ainda.
export function esperadoAteOntem(alvo: number, hoje: Date, inicio: string, fim: string): number {
  const ontem = dataISOLocal(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 1))
  if (ontem >= fim) return arredondar2(alvo)
  const total = diasUteisInclusive(inicio, fim)
  if (total === 0) return 0
  return arredondar2((alvo * diasUteisInclusive(inicio, ontem)) / total)
}

// Decisao dela 01/10: em risco = mais de 20% abaixo do esperado ate ontem.
export const MARGEM_DO_RISCO = 0.8

export function situacaoDaMeta(meta: Meta, realizado: number, hoje: Date): SituacaoDaMeta {
  if (realizado >= meta.valor_alvo) return 'atingida'
  const esperado = esperadoAteOntem(meta.valor_alvo, hoje, meta.periodo_inicio, meta.periodo_fim)
  return realizado < esperado * MARGEM_DO_RISCO ? 'em_risco' : 'em_andamento'
}

export function contarSituacoes(lista: Array<{ meta: Meta; realizado: number }>, hoje: Date): Record<SituacaoDaMeta, number> {
  const contagem: Record<SituacaoDaMeta, number> = { atingida: 0, em_risco: 0, em_andamento: 0 }
  for (const m of lista) contagem[situacaoDaMeta(m.meta, m.realizado, hoje)]++
  return contagem
}

// O MESMO estagio da lista de Pedidos (estagioDoPedido): o numero do Inicio e o
// que a lista mostra depois do toque.
export const ESTAGIOS_AGUARDANDO_ENTREGA: Estagio[] = ['enviado', 'faturado']
export const ESTAGIOS_EM_ABERTO: Estagio[] = ['rascunho']

export function contarPedidosNosEstagios(pedidos: Pedido[], subPedidos: SubPedido[], estagios: Estagio[]): number {
  return pedidos.filter((p) => p.apagado_em === null &&
    estagios.includes(estagioDoPedido(subPedidos.filter((s) => s.pedido_id === p.id)))).length
}

// Segunda-feira da semana de `hoje` (a semana do app comeca na segunda).
function segundaDaSemana(hoje: Date): Date {
  const d = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return d
}

// Cliente ativo cujo dia de visita ja passou nesta semana (segunda ate ONTEM)
// e que nao teve nenhuma visita desde segunda. Datas no fuso LOCAL: a
// `data_hora` e ISO em UTC (ver bug-visita-fuso-utc).
export function clientesSemVisitaNaSemana(clientes: Cliente[], visitas: Visita[], hoje: Date): Cliente[] {
  const segunda = segundaDaSemana(hoje)
  const segundaISO = dataISOLocal(segunda)
  const diasQueJaPassaram = new Set<number>()
  for (let d = new Date(segunda); dataISOLocal(d) < dataISOLocal(hoje); d.setDate(d.getDate() + 1)) diasQueJaPassaram.add(d.getDay())
  const visitados = new Set(visitas
    .filter((v) => v.apagado_em === null && dataISOLocal(new Date(v.data_hora)) >= segundaISO)
    .map((v) => v.cliente_id))
  return clientes.filter((c) => c.apagado_em === null && c.status === 'ativo' && c.dia_visita !== null &&
    diasQueJaPassaram.has(c.dia_visita) && !visitados.has(c.id))
}

// Decisao dela 02/10: o "cliente estrategico" do desenho e o cliente OURO
// (potencial A). Pendencia = Ouro ativo que ainda nao comprou no mes de hoje.
// "Comprou" e a mesma venda da positivacao (metasDados.ts): pedido fechado com
// marca em STATUS_VENDA -- rascunho e cancelado nao contam.
export function clientesOuroSemPedidoNoMes(clientes: Cliente[], pedidos: Pedido[], subPedidos: SubPedido[], hoje: Date): Cliente[] {
  const inicio = dataISOLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1))
  const fim = dataISOLocal(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0))
  const comVenda = new Set(subPedidos.filter((s) => s.apagado_em === null && STATUS_VENDA.includes(s.status)).map((s) => s.pedido_id))
  const compraram = new Set(pedidos
    .filter((p) => p.apagado_em === null && p.status === 'fechado' && dentroDoPeriodo(p.data, inicio, fim) && comVenda.has(p.id))
    .map((p) => p.cliente_id))
  return clientes.filter((c) => c.apagado_em === null && c.status === 'ativo' && c.potencial === 'A' && !compraram.has(c.id))
}

// Decisao dela 01/10 (c2): "Nenhum pedido", "1 pedido", "3 pedidos" -- sem o
// "(s)" sobrando.
export function textoDaContagem(n: number, palavras: { nenhum: string; um: string; varios: string }): string {
  if (n === 0) return palavras.nenhum
  return `${n} ${n === 1 ? palavras.um : palavras.varios}`
}

// Spec 2026-10-02 (ciclo 2A): a faixa da rota no card Minha Agenda. Recebe o
// que clientesDoDia/visitasDeHoje devolvem -- os MESMOS da aba Agenda, entao o
// numero do Inicio e sempre o da rota. Visita de cliente fora da rota nao conta.
export function resumoDaRota(clientes: Cliente[], visitas: Map<string, Visita>): { total: number; visitados: number } {
  return { total: clientes.length, visitados: clientes.filter((c) => visitas.has(c.id)).length }
}
